import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { AvatarPersona } from '@xiltrion/ui/bloques/personas';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@xiltrion/ui/field';
import { SelectorMultiple } from '@xiltrion/ui/bloques/selectores';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { InputClave } from '@xiltrion/ui/input-clave';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@xiltrion/ui/input-otp';
import { avisar } from '@xiltrion/ui/sonner';
import { Switch } from '@xiltrion/ui/switch';
import { apiDelete, apiGet, apiPatch, apiPost, errorMessage } from '@/api/client';
import { useSession } from '@/app/session';
import { setThemeMode } from '@/app/theme';

const avisarError = (e: unknown) => avisar.error(errorMessage(e));

/** Mi perfil: verificación en dos pasos, contraseña, sesiones abiertas, entidades y preferencias. */
export function Profile() {
  const qc = useQueryClient();
  const { me, refresh } = useSession();
  const [setup, setSetup] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [pw, setPw] = useState({ current: '', password: '' });
  const { data: sessions = [] } = useQuery({ queryKey: ['sessions'], queryFn: () => apiGet<Array<{ id: string; createdAt: string; lastActivityAt: string; ip: string | null; userAgent: string | null; current: boolean }>>('/me/sessions') });
  if (!me) return null;
  const myApps = [...new Map(me.entities.flatMap((e) => e.apps).map((a) => [a.code, a])).values()];
  const pref = (k: string, v: unknown) => void apiPatch('/me/preferences', { [k]: v }).then(() => refresh(), avisarError);
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <EncabezadoPagina titulo="Mi perfil" />
      <Card>
        <CardContent className="flex items-center gap-4">
          <AvatarPersona persona={{ nombre: me.displayName, color: me.avatarColor }} tamano="lg" />
          <div><div className="font-medium">{me.displayName}</div><div className="text-sm text-muted-foreground">{me.email}</div><div className="mt-1 flex flex-wrap gap-1">{me.platformRole === 'SUPERADMIN' && <Badge variant="default">Super</Badge>}{me.entities.map((e) => <Badge key={e.slug} variant="secondary">{e.name} · {e.role === 'ADMIN' ? 'admin' : 'miembro'}</Badge>)}</div></div>
        </CardContent>
      </Card>
      {/* Para todos: con contraseña protege el ingreso; con Entra o Google (que ya protegen el ingreso) sirve para firmar en las apps. */}
      <Card>
        <CardHeader><CardTitle className="text-base">Verificación en dos pasos</CardTitle><CardDescription>Con una app autenticadora (Google Authenticator, Microsoft Authenticator, Authy). {me.authProvider === 'local' ? 'Protege el ingreso a todas tus apps y' : 'Tu ingreso lo protege tu cuenta institucional; la app autenticadora'} sirve para firmar documentos en las apps sin esperar un código por correo.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          {me.mfaEnabled ? <Alert variant="success"><Icono nombre="mdi:check-circle-outline" /><AlertTitle>Activa</AlertTitle></Alert> : !setup ? (
            <>
              {me.mfaSetupRequired && <Alert variant="warning"><Icono nombre="mdi:alert-outline" /><AlertDescription>Debes activarla para poder hacer cambios.</AlertDescription></Alert>}
              <Button onClick={() => void apiPost<{ qrDataUrl: string; secret: string }>('/me/mfa/setup').then(setSetup, avisarError)}>Activar</Button>
            </>
          ) : codes ? (
            <Alert variant="info"><AlertTitle>Guarda tus códigos de recuperación</AlertTitle><AlertDescription><p className="mb-2">Cada uno sirve una vez si pierdes el teléfono. No los volverás a ver.</p><div className="tnum grid grid-cols-2 gap-1 font-mono text-sm">{codes.map((c) => <span key={c}>{c}</span>)}</div></AlertDescription></Alert>
          ) : (
            <div className="flex flex-wrap items-start gap-6">
              <img src={setup.qrDataUrl} alt="Código QR para la app autenticadora" className="size-44 rounded-md border bg-white p-2" />
              <div className="space-y-3">
                <p className="text-sm">Escanea el código y escribe el número que muestra la app.</p>
                <p className="text-xs text-muted-foreground">¿No puedes escanear? Clave: <code className="break-all">{setup.secret}</code></p>
                <InputOTP maxLength={6} value={code} onChange={setCode}><InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup></InputOTP>
                <Button disabled={code.length !== 6} onClick={() => void apiPost<{ recoveryCodes: string[] }>('/me/mfa/enable', { code }).then((r) => { setCodes(r.recoveryCodes); void refresh(); }, avisarError)}>Confirmar</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {me.authProvider === 'local' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Contraseña</CardTitle><CardDescription>Al cambiarla se cierran tus otras sesiones.</CardDescription></CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void apiPost('/me/password', pw).then(() => { avisar.success('Contraseña cambiada'); setPw({ current: '', password: '' }); }, avisarError); }}>
              <FieldGroup>
                <Field><FieldLabel htmlFor="cur">Actual</FieldLabel><InputClave id="cur" required value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" /></Field>
                <Field><FieldLabel htmlFor="new">Nueva (mínimo 10 caracteres)</FieldLabel><InputClave id="new" minLength={10} required value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} autoComplete="new-password" /></Field>
              </FieldGroup>
              <Button type="submit" variant="outline">Cambiar</Button>
            </form>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader><CardTitle className="text-base">Preferencias</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center justify-between gap-4 text-sm"><span>Modo oscuro</span><Switch checked={me.preferences.theme === 'dark'} onCheckedChange={(v) => { setThemeMode(v ? 'dark' : 'light'); pref('theme', v ? 'dark' : 'light'); }} /></label>
          <label className="flex items-center justify-between gap-4 text-sm"><span>Recibir los avisos también por correo</span><Switch checked={me.preferences.emailNotifications !== false} onCheckedChange={(v) => pref('emailNotifications', v)} /></label>
          {me.preferences.emailNotifications !== false && myApps.length > 0 && (
            <Field>
              <FieldLabel>Apps cuyos avisos no quiero por correo</FieldLabel>
              <SelectorMultiple value={me.preferences.emailMutedApps ?? []} placeholder="Ninguna: todos por correo" options={myApps.map((a) => ({ value: a.code, label: a.name }))} onChange={(v) => pref('emailMutedApps', v)} />
              <FieldDescription>Esos avisos siguen llegando a la campana. Vale en todas las apps de la suite.</FieldDescription>
            </Field>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Sesiones abiertas</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
              <div className="min-w-0"><div className="truncate">{s.userAgent ?? 'Navegador'}{s.current && <Badge size="sm" variant="primary-light" className="ml-2">esta</Badge>}</div><div className="text-xs text-muted-foreground">{s.ip ?? ''} · última actividad {new Date(s.lastActivityAt).toLocaleString('es-CO')}</div></div>
              {!s.current && <Button size="sm" variant="ghost" onClick={() => void apiDelete(`/me/sessions/${s.id}`).then(() => qc.invalidateQueries({ queryKey: ['sessions'] }))}>Cerrar</Button>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
